/**
 * APTS Admin Control Panel
 * - Users/subscriptions listing with filters
 * - Grant / Lock / Unlock / Extend actions
 * - Run daily cron manually
 * - Revenue stats
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Shield, Gift, Lock, Unlock, Clock, RefreshCw, BarChart3, X, Search, Check, Eye, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import BulkMessenger from '../../components/BulkMessenger';

export default function APTSAdmin() {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState('active');
  const [plans, setPlans] = useState([]);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantQuery, setGrantQuery] = useState('');
  const [grantResults, setGrantResults] = useState([]);
  const [grantUser, setGrantUser] = useState(null);
  const [grantPlan, setGrantPlan] = useState('monthly');
  const [grantDays, setGrantDays] = useState('');
  const [grantBusy, setGrantBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, u, p] = await Promise.all([
        api.get('/apts/admin/stats'),
        api.get(`/apts/admin/users?limit=200${filter !== 'all' ? `&status=${filter}` : ''}`),
        api.get('/apts/plans'),
      ]);
      setStats(s.data); setSubs(u.data.subscriptions || []); setPlans(p.data.plans || []);
    } catch { /* ignore */ }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  // Debounced user search (reuse safety-qr admin users search)
  useEffect(() => {
    if (!grantOpen) return;
    const q = grantQuery.trim(); if (q.length < 1) { setGrantResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get('/safety-qr/admin/users/search', { params: { q, limit: 8 } }); setGrantResults(r.data.users || []); }
      catch { setGrantResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [grantQuery, grantOpen]);

  const openGrant = () => { setGrantUser(null); setGrantQuery(''); setGrantResults([]); setGrantPlan('monthly'); setGrantDays(''); setGrantOpen(true); };
  const submitGrant = async () => {
    if (!grantUser) return toast.error('User select karein');
    setGrantBusy(true);
    try {
      const payload = { user_id: grantUser.id, plan_id: grantPlan };
      if (grantDays) payload.days = parseInt(grantDays);
      const r = await api.post('/apts/admin/grant', payload);
      toast.success(`✅ Granted ${grantPlan} to @${grantUser.username} till ${r.data.subscription.end_date.slice(0, 10)}`);
      setGrantOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setGrantBusy(false); }
  };

  const lockUser = async (uid) => {
    if (!window.confirm('Lock this user access?')) return;
    try { await api.post('/apts/admin/lock', { user_id: uid }); toast.success('Locked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const unlockUser = async (uid) => {
    try { await api.post('/apts/admin/unlock', { user_id: uid }); toast.success('Unlocked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const extendUser = async (uid) => {
    const d = parseInt(prompt('Extend by how many days?', '30'));
    if (!d || d < 1) return;
    try { const r = await api.post('/apts/admin/extend', { user_id: uid, days: d }); toast.success(`Extended till ${r.data.new_end_date.slice(0, 10)}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const runCron = async () => {
    if (!window.confirm('Run daily cron now (lock expired, scan dues)?')) return;
    try { const r = await api.post('/apts/admin/run-cron'); toast.success(`Cron: ${r.data.expired_locked} locked, ${r.data.upcoming_dues} due`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ============ User Detail Modal ============
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const openDetail = async (uid) => {
    setDetailBusy(true);
    try { const r = await api.get(`/apts/admin/user/${uid}/detail`); setDetail(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setDetailBusy(false); }
  };
  const resetScope = async (uid, scope) => {
    if (!window.confirm(`Delete all ${scope.toUpperCase()} data for this user? This cannot be undone.`)) return;
    try {
      const r = await api.post('/apts/admin/user/reset', { user_id: uid, scope });
      toast.success(`Reset done: ${JSON.stringify(r.data.deleted)}`);
      openDetail(uid); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white pb-16">
      <div className="max-w-6xl mx-auto p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-400" />
            <h1 className="text-2xl font-extrabold">APTS Admin Control</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setBulkOpen(true)} disabled={subs.length === 0} data-testid="apts-admin-bulk-btn" className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40">📢 Bulk Message</button>
            <button onClick={openGrant} data-testid="apts-admin-grant-btn" className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center gap-1"><Gift className="w-3 h-3" /> Grant Plan</button>
            <button onClick={runCron} data-testid="apts-admin-cron-btn" className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Run Cron</button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <Stat label="Active Subs" value={stats.active} tone="emerald" />
            <Stat label="Active Trials" value={stats.active_trials} tone="indigo" />
            <Stat label="Expired" value={stats.expired} tone="red" />
            <Stat label="Locked" value={stats.locked} tone="amber" />
            <Stat label="Ledger Entries" value={stats.ledger_entries} tone="slate" />
            <Stat label="Items Tracked" value={stats.items_tracked} tone="slate" />
            <Stat label="Goals Active" value={stats.goals_active} tone="slate" />
            <Stat label="Admin Revenue (AT)" value={stats.admin_revenue_at.toFixed(2)} tone="fuchsia" />
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-1 mb-3 overflow-x-auto">
          {['all', 'active', 'expired', 'locked', 'superseded'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 rounded text-[11px] font-bold capitalize whitespace-nowrap ${filter === f ? 'bg-indigo-500 text-white' : 'bg-white/10 text-slate-300'}`}>{f}</button>
          ))}
        </div>

        {/* Table */}
        <div className="rounded-xl border border-white/10 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-white/5 text-xs uppercase text-slate-400">
              <tr><th className="text-left px-3 py-2">User</th><th className="px-3 py-2">Plan</th><th className="px-3 py-2">Price</th><th className="px-3 py-2">End Date</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Actions</th></tr>
            </thead>
            <tbody>
              {subs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-xs">Koi subscription nahi</td></tr>}
              {subs.map((s) => (
                <tr key={s.id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-3 py-2"><b>@{s.user?.username || '—'}</b><div className="text-[10px] text-slate-400">{s.user?.email}</div></td>
                  <td className="px-3 py-2 text-xs">{s.plan_name}</td>
                  <td className="px-3 py-2 text-center text-xs">{s.price_at} AT</td>
                  <td className="px-3 py-2 text-center text-xs">{s.end_date ? new Date(s.end_date).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-3 py-2 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : s.status === 'locked' ? 'bg-amber-500/20 text-amber-300' : s.status === 'expired' ? 'bg-red-500/20 text-red-300' : 'bg-slate-500/20 text-slate-400'}`}>{s.status}</span></td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-center">
                      <button onClick={() => openDetail(s.user_id)} data-testid={`apts-view-${s.user_id}`} className="p-1 rounded bg-indigo-500/20 text-indigo-300" title="View full data"><Eye className="w-3 h-3" /></button>
                      {s.status === 'active' && <button onClick={() => lockUser(s.user_id)} className="p-1 rounded bg-amber-500/20 text-amber-300" title="Lock"><Lock className="w-3 h-3" /></button>}
                      {s.status === 'locked' && <button onClick={() => unlockUser(s.user_id)} className="p-1 rounded bg-emerald-500/20 text-emerald-300" title="Unlock"><Unlock className="w-3 h-3" /></button>}
                      {s.status === 'active' && <button onClick={() => extendUser(s.user_id)} className="p-1 rounded bg-indigo-500/20 text-indigo-300" title="Extend"><Clock className="w-3 h-3" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Grant modal */}
        {grantOpen && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !grantBusy && setGrantOpen(false)}>
            <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-emerald-500/30 p-5" onClick={(e) => e.stopPropagation()} data-testid="apts-grant-dialog">
              <div className="flex justify-between items-center mb-3"><h3 className="text-lg font-extrabold">Grant APTS Plan</h3><button onClick={() => setGrantOpen(false)}><X className="w-4 h-4" /></button></div>
              <label className="text-[11px] uppercase font-bold text-slate-400">User (username/email/name)</label>
              <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" /><input value={grantQuery} onChange={(e) => { setGrantQuery(e.target.value); setGrantUser(null); }} placeholder="anant1981" className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" autoFocus data-testid="apts-grant-user-search" /></div>
              {grantUser ? (
                <div className="mt-2 flex items-center justify-between rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2"><div><div className="text-sm font-bold">@{grantUser.username}</div><div className="text-[10px] text-slate-400">{grantUser.email}</div></div><Check className="w-4 h-4 text-emerald-400" /></div>
              ) : grantResults.length > 0 && (
                <div className="mt-2 rounded-lg border border-white/10 max-h-40 overflow-y-auto">
                  {grantResults.map((u) => (
                    <button key={u.id} onClick={() => { setGrantUser(u); setGrantQuery(u.username); }} className="w-full text-left px-3 py-2 hover:bg-emerald-500/10 border-b border-white/5 last:border-0">
                      <div className="text-sm font-bold">@{u.username}</div><div className="text-[10px] text-slate-400">{u.email}</div>
                    </button>
                  ))}
                </div>
              )}
              <label className="block text-[11px] uppercase font-bold text-slate-400 mt-3">Plan</label>
              <div className="grid grid-cols-2 gap-1.5">
                {plans.filter((p) => !p.trial).map((p) => (
                  <button key={p.id} onClick={() => setGrantPlan(p.id)} data-testid={`apts-grant-plan-${p.id}`} className={`py-1.5 px-2 rounded-lg text-xs font-bold ${grantPlan === p.id ? 'bg-emerald-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}>{p.name} ({p.duration_days}d)</button>
                ))}
              </div>
              <label className="block text-[11px] uppercase font-bold text-slate-400 mt-3">Override days (optional)</label>
              <input type="number" placeholder="Leave blank for plan default" value={grantDays} onChange={(e) => setGrantDays(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm" />
              <button onClick={submitGrant} disabled={!grantUser || grantBusy} data-testid="apts-grant-submit" className="w-full mt-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm disabled:opacity-40">{grantBusy ? 'Granting…' : 'Grant Now'}</button>
            </div>
          </div>
        )}

        {/* User Detail Modal */}
        {detail && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
            <div className="w-full max-w-3xl rounded-2xl bg-slate-900 border border-indigo-400/40 p-5 my-8" onClick={(e) => e.stopPropagation()} data-testid="apts-detail-modal">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h3 className="text-lg font-extrabold">👤 @{detail.user?.username} — Full APTS Data</h3>
                  <div className="text-[10px] text-slate-400">{detail.user?.email}</div>
                </div>
                <button onClick={() => setDetail(null)} className="p-1"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <div className="rounded bg-emerald-500/10 p-2 text-center"><div className="text-[10px] uppercase">Net Worth</div><div className="text-lg font-extrabold text-emerald-300">₹{detail.totals?.net_worth}</div></div>
                <div className="rounded bg-white/5 p-2 text-center"><div className="text-[10px] uppercase">Accounts</div><div className="text-lg font-extrabold">₹{detail.accounts_total}</div></div>
                <div className="rounded bg-white/5 p-2 text-center"><div className="text-[10px] uppercase">Ledger</div><div className="text-lg font-extrabold">{detail.ledger_count}</div></div>
                <div className="rounded bg-white/5 p-2 text-center"><div className="text-[10px] uppercase">Items</div><div className="text-lg font-extrabold">{detail.items?.length}</div></div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3">
                <button onClick={() => resetScope(detail.user.id, 'ledger')} className="text-[10px] px-2 py-1.5 rounded bg-red-500/20 text-red-300 font-bold flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> Ledger</button>
                <button onClick={() => resetScope(detail.user.id, 'items')} className="text-[10px] px-2 py-1.5 rounded bg-red-500/20 text-red-300 font-bold flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> Items</button>
                <button onClick={() => resetScope(detail.user.id, 'goals')} className="text-[10px] px-2 py-1.5 rounded bg-red-500/20 text-red-300 font-bold flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> Goals</button>
                <button onClick={() => resetScope(detail.user.id, 'receivables')} className="text-[10px] px-2 py-1.5 rounded bg-red-500/20 text-red-300 font-bold flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> Receivables</button>
                <button onClick={() => resetScope(detail.user.id, 'notes')} className="text-[10px] px-2 py-1.5 rounded bg-red-500/20 text-red-300 font-bold flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> Notes</button>
                <button onClick={() => resetScope(detail.user.id, 'accounts')} className="text-[10px] px-2 py-1.5 rounded bg-red-500/20 text-red-300 font-bold flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> Accounts</button>
                <button onClick={() => resetScope(detail.user.id, 'all')} className="col-span-2 sm:col-span-3 text-[10px] px-2 py-1.5 rounded bg-red-600 text-white font-bold flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> RESET ALL (except subscription)</button>
              </div>

              <details className="mb-2"><summary className="cursor-pointer text-xs font-bold text-slate-300">💰 Ledger ({detail.ledger?.length})</summary>
                <div className="mt-1 max-h-40 overflow-y-auto text-[11px]">
                  {(detail.ledger || []).slice(0, 30).map((e) => <div key={e.id} className="flex justify-between border-b border-white/5 py-1"><span>{e.date} · {e.category} <span className="text-slate-500">({e.transaction_type})</span></span><span className="font-bold">₹{e.amount}</span></div>)}
                </div>
              </details>
              <details className="mb-2"><summary className="cursor-pointer text-xs font-bold text-slate-300">🏦 Accounts ({detail.accounts?.length})</summary>
                <div className="mt-1 text-[11px]">{(detail.accounts || []).map((a) => <div key={a.id} className="flex justify-between border-b border-white/5 py-1"><span>{a.name} ({a.account_type})</span><span className="font-bold">₹{a.balance}</span></div>)}</div>
              </details>
              <details className="mb-2"><summary className="cursor-pointer text-xs font-bold text-slate-300">💸 Receivables ({detail.receivables?.length})</summary>
                <div className="mt-1 text-[11px]">{(detail.receivables || []).map((r) => <div key={r.id} className="flex justify-between border-b border-white/5 py-1"><span>{r.type === 'receivable' ? '💚' : '🔴'} {r.party_name}</span><span>₹{r.remaining || r.amount} · {r.status}</span></div>)}</div>
              </details>
              <details className="mb-2"><summary className="cursor-pointer text-xs font-bold text-slate-300">📦 Items ({detail.items?.length})</summary>
                <div className="mt-1 text-[11px]">{(detail.items || []).map((i) => <div key={i.id} className="flex justify-between border-b border-white/5 py-1"><span>{i.type}: {i.name}</span><span>₹{i.principal_value}</span></div>)}</div>
              </details>
              <details className="mb-2"><summary className="cursor-pointer text-xs font-bold text-slate-300">🎯 Goals ({detail.goals?.length})</summary>
                <div className="mt-1 text-[11px]">{(detail.goals || []).map((g) => <div key={g.id} className="flex justify-between border-b border-white/5 py-1"><span>{g.title}</span><span>₹{g.saved_amount}/₹{g.target_amount}</span></div>)}</div>
              </details>
              <details><summary className="cursor-pointer text-xs font-bold text-slate-300">📝 Notes ({detail.notes?.length})</summary>
                <div className="mt-1 text-[11px]">{(detail.notes || []).map((n) => <div key={n.id} className="border-b border-white/5 py-1">{n.pinned && '📌 '}{n.title}</div>)}</div>
              </details>
            </div>
          </div>
        )}
      </div>
      <BulkMessenger open={bulkOpen} onClose={() => setBulkOpen(false)} recipients={subs.map((s) => ({ id: s.id, name: s.user?.full_name || s.user?.username, mobile: s.user?.mobile || s.user?.phone }))} defaultTemplate={'Namaste {name},\n\nAapki APTS subscription se related important update hai. Kripya app par visit karein.\n\nDhanyawad 🙏'} label="APTS Users ko Bulk Message" />
    </div>
  );
}

const Stat = ({ label, value, tone }) => {
  const toneCls = { emerald: 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10', red: 'text-red-300 border-red-400/30 bg-red-500/10', amber: 'text-amber-300 border-amber-400/30 bg-amber-500/10', indigo: 'text-indigo-300 border-indigo-400/30 bg-indigo-500/10', fuchsia: 'text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-500/10', slate: 'text-slate-300 border-white/10 bg-white/5' };
  return (
    <div className={`rounded-xl border p-3 ${toneCls[tone] || toneCls.slate}`}>
      <div className="text-[10px] uppercase font-bold opacity-70">{label}</div>
      <div className="text-2xl font-extrabold mt-0.5">{value}</div>
    </div>
  );
};
